import React, { useEffect, useMemo, useRef } from "react";
import {
  lightenHexColor,
  themeButtonBackground,
  themeButtonTextColor,
} from "../../utils/colorUtils";
import "./talentThemedModal.css";

export default function TalentThemedModal({
  open,
  title,
  titleId = "ytm-title",
  themeData,
  confirmText = "Got it",
  onConfirm,
  closeOnBackdrop = true,
  autoCloseMs = 0,
  children,
}) {
  const timerRef = useRef(null);
  const confirmRef = useRef(null);
  const titleColor = themeData?.text_color || "#1a2b6d";
  const buttonBg = themeButtonBackground(themeData);
  const buttonText = themeButtonTextColor(themeData);
  const modalBg = useMemo(
    () => (buttonBg ? lightenHexColor(buttonBg, 0.82) : null),
    [buttonBg]
  );
  const btnStyle = {
    ...(buttonBg && { backgroundColor: buttonBg }),
    ...(buttonText && { color: buttonText }),
    ...(themeData?.button_bordercolor && { borderColor: themeData.button_bordercolor }),
    ...(buttonText && { border: `2px solid ${buttonText}` }),
  };
  const modalStyle = {
    "--ytm-text-color": titleColor,
    ...(modalBg ? { "--ytm-modal-bg": modalBg } : {}),
  };

  useEffect(() => {
    if (!open || !autoCloseMs || !onConfirm) return undefined;
    timerRef.current = window.setTimeout(onConfirm, autoCloseMs);
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, autoCloseMs, onConfirm]);

  // Keyboard users: move focus into the dialog and let Escape close it.
  useEffect(() => {
    if (!open) return undefined;
    confirmRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape" && closeOnBackdrop && onConfirm) onConfirm();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, closeOnBackdrop, onConfirm]);

  if (!open) return null;

  return (
    <>
      <div
        className="ytm-backdrop"
        onClick={closeOnBackdrop ? onConfirm : undefined}
        aria-hidden
      />
      <div
        className={`ytm-modal${modalBg ? " ytm-modal--themed" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={modalStyle}
      >
        {autoCloseMs > 0 ? (
          <div className="ytm-timer-bar" aria-hidden>
            <span
              className="ytm-timer-bar-fill"
              style={{
                backgroundColor: buttonBg || "#22c55e",
                animationDuration: `${autoCloseMs}ms`,
              }}
            />
          </div>
        ) : null}
        <h3 id={titleId}>
          {title}
        </h3>
        <div className="ytm-body">{children}</div>
        <button
          ref={confirmRef}
          type="button"
          className="ytm-confirm ytm-confirm--themed"
          style={btnStyle.backgroundColor || btnStyle.color ? btnStyle : undefined}
          onClick={onConfirm}
        >
          {confirmText}
        </button>
      </div>
    </>
  );
}
